/**
 * ROSEVIBE — commerce interactions: AJAX cart, variant selection,
 * quantity steppers, product gallery. Motion/chrome (drawers, reveals,
 * header state) lives in animations.js; this file calls into
 * window.RV.openCartDrawer() after successful cart mutations.
 */
(function () {
  'use strict';

  var routes = {
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js'
  };

  /* --------------------------------------------------------------------
   * Cart drawer content refresh
   * ------------------------------------------------------------------ */
  function refreshCartDrawer(sectionsHtml) {
    var content = document.getElementById('rv-cart-drawer-content');
    if (content && sectionsHtml && sectionsHtml['cart-drawer']) {
      content.innerHTML = sectionsHtml['cart-drawer'];
    }
  }

  function refreshCartPage(sectionsHtml) {
    var content = document.getElementById('rv-cart-page-content');
    if (!content || !sectionsHtml || !sectionsHtml['main']) return;
    var temp = document.createElement('div');
    temp.innerHTML = sectionsHtml['main'];
    var fresh = temp.querySelector('#rv-cart-page-content');
    if (fresh) content.innerHTML = fresh.innerHTML;
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-rv-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function postCart(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  /* --------------------------------------------------------------------
   * Add to cart (product form)
   * ------------------------------------------------------------------ */
  function initAddToCart() {
    var form = document.getElementById('rv-product-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // more than one add-to-cart control can exist for the same form (the
      // in-page button plus the mobile sticky bar's button living outside
      // the <form> via form="rv-product-form") — keep all of them in sync.
      var buttons = document.querySelectorAll('[data-rv-add-to-cart]');
      var labels = document.querySelectorAll('[data-rv-add-label]');
      var originalLabel = labels.length ? labels[0].textContent : '';
      var formData = new FormData(form);

      buttons.forEach(function (btn) { btn.setAttribute('disabled', 'disabled'); });
      labels.forEach(function (lbl) { lbl.textContent = 'Adding…'; });

      postCart(routes.cartAdd, {
        id: formData.get('id'),
        quantity: formData.get('quantity') || 1,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
        .then(function (data) {
          refreshCartDrawer(data.sections);
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          if (document.body.getAttribute('data-cart-type') === 'page') {
            window.location.href = '/cart';
            return;
          }
          if (window.RV && window.RV.openCartDrawer) window.RV.openCartDrawer();
        })
        .catch(function () {
          labels.forEach(function (lbl) { lbl.textContent = 'Couldn’t Add — Try Again'; });
        })
        .finally(function () {
          buttons.forEach(function (btn) { btn.removeAttribute('disabled'); });
          if (originalLabel) {
            setTimeout(function () {
              labels.forEach(function (lbl) { lbl.textContent = originalLabel; });
            }, 2200);
          }
        });
    });
  }

  /* --------------------------------------------------------------------
   * Cart drawer / page line item mutations (event delegation — survives
   * innerHTML swaps from the AJAX refresh above)
   * ------------------------------------------------------------------ */
  function changeLine(line, quantity) {
    var onCartPage = !!document.querySelector('[data-rv-cart-page]');
    var sections = onCartPage ? ['cart-drawer', 'main'] : ['cart-drawer'];
    return postCart(routes.cartChange, {
      line: line,
      quantity: quantity,
      sections: sections,
      sections_url: window.location.pathname
    }).then(function (data) {
      refreshCartDrawer(data.sections);
      if (onCartPage) refreshCartPage(data.sections);
      updateCartCount(data.item_count);
    });
  }

  function initCartLineEvents() {
    document.addEventListener('click', function (e) {
      var incr = e.target.closest('[data-rv-cart-qty-increase]');
      var decr = e.target.closest('[data-rv-cart-qty-decrease]');
      var remove = e.target.closest('[data-rv-cart-remove]');

      if (incr || decr) {
        var btn = incr || decr;
        var line = parseInt(btn.getAttribute('data-line'), 10);
        var valueEl = btn.parentElement.querySelector('[data-rv-cart-qty-value]');
        var current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        var next = incr ? current + 1 : Math.max(0, current - 1);
        changeLine(line, next);
      }

      if (remove) {
        var removeLine = parseInt(remove.getAttribute('data-line'), 10);
        changeLine(removeLine, 0);
      }
    });
  }

  /* --------------------------------------------------------------------
   * Product page: option heading correction
   *
   * Supplier-imported products often carry every option under generic
   * names ("Color", "Size") regardless of what the values actually are —
   * e.g. a "Color" option whose values are "100ml" / "2Style" (a volume
   * and a style-count crammed into one option), or a "Size" option whose
   * values are "3PCS" / "2PCS" / "1PC" (actually a quantity). The heading
   * is static text from Shopify admin; the values are the only reliable
   * signal for what an option actually represents.
   *
   * Each value is classified independently, then:
   *   - if every value agrees on one category (all volumes, all
   *     quantities, etc.) the heading becomes that specific word
   *   - if they classify as different non-color categories (the 100ml /
   *     2Style case), the heading falls back to the neutral "Option" —
   *     still more honest than "Color", without inventing a false label
   *     for a mixed bag
   *   - if any value doesn't match a known pattern at all (plausibly a
   *     real color, or something not covered here), the heading is left
   *     exactly as the merchant named it
   * ------------------------------------------------------------------ */
  function initOptionLabels() {
    var patterns = [
      { re: /^\d+(\.\d+)?\s*(ml|l|oz|fl\.?\s?oz|g|kg|lbs?)$/i, label: 'Volume' },
      { re: /^\d+\s*(pcs?|pieces?|packs?)\.?$/i, label: 'Quantity' },
      { re: /^\d+\s*styles?$/i, label: 'Style' }
    ];

    function classify(value) {
      var found = patterns.find(function (pattern) { return pattern.re.test(value); });
      return found ? found.label : null;
    }

    document.querySelectorAll('.rv-pdp__option').forEach(function (fieldset) {
      var legend = fieldset.querySelector('.rv-pdp__label');
      var inputs = fieldset.querySelectorAll('[data-rv-option-selector]');
      if (!legend || !inputs.length) return;

      var values = Array.prototype.map.call(inputs, function (input) {
        return input.value.trim();
      });

      var labels = values.map(classify);
      if (labels.indexOf(null) !== -1) return;

      var unique = labels.filter(function (label, i) { return labels.indexOf(label) === i; });
      legend.textContent = unique.length === 1 ? unique[0] : 'Option';
    });
  }

  /* --------------------------------------------------------------------
   * Product page: quantity stepper
   * ------------------------------------------------------------------ */
  function initQuantityStepper() {
    // Was '.rv-product__quantity', a class that doesn't exist anywhere in
    // the current markup — the +/- buttons silently did nothing (the native
    // number input's own spinner was the only way to change quantity).
    var wrapper = document.querySelector('.rv-qty-stepper');
    if (!wrapper) return;
    var input = wrapper.querySelector('#rv-qty');
    var decrease = wrapper.querySelector('[data-rv-qty-decrease]');
    var increase = wrapper.querySelector('[data-rv-qty-increase]');
    if (!input) return;

    decrease && decrease.addEventListener('click', function () {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
    });
    increase && increase.addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    });
  }

  /* --------------------------------------------------------------------
   * Product page: variant selection
   * ------------------------------------------------------------------ */
  function initVariantSelection() {
    var section = document.querySelector('[data-rv-product-section]');
    if (!section) return;

    var variantScript = section.querySelector('[data-rv-product-variants]');
    if (!variantScript) return;

    var variants;
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (err) {
      return;
    }
    if (!variants.length) return;

    var optionInputs = section.querySelectorAll('[data-rv-option-selector]');
    var hiddenIdInput = section.querySelector('[data-rv-selected-variant-id]');
    // price target and add-to-cart controls can exist twice — the in-page
    // one plus the mobile sticky bar, which lives outside <section> —
    // so these are document-scoped and updated in lockstep.
    var priceTargets = document.querySelectorAll('[data-rv-price-target]');
    var addButtons = document.querySelectorAll('[data-rv-add-to-cart]');
    var addLabels = document.querySelectorAll('[data-rv-add-label]');
    var gallerySlides = section.querySelectorAll('[data-rv-gallery-main] [data-media-id]');
    var thumbs = section.querySelectorAll('[data-rv-thumb]');
    var galleryPrev = section.querySelector('[data-rv-gallery-prev]');
    var galleryNext = section.querySelector('[data-rv-gallery-next]');
    var galleryCounter = section.querySelector('[data-rv-gallery-counter-current]');
    var mediaIds = Array.prototype.map.call(gallerySlides, function (slide) {
      return slide.getAttribute('data-media-id');
    });

    function currentSelection() {
      var selection = {};
      optionInputs.forEach(function (input) {
        if (input.checked) selection[input.getAttribute('data-option-position')] = input.value;
      });
      return selection;
    }

    function findMatchingVariant(selection) {
      var positions = Object.keys(selection).sort();
      return variants.find(function (variant) {
        return positions.every(function (pos) {
          var idx = parseInt(pos, 10) - 1;
          return variant.options[idx] === selection[pos];
        });
      });
    }

    function activateMedia(mediaId) {
      if (!mediaId) return;
      mediaId = String(mediaId);
      gallerySlides.forEach(function (slide) {
        slide.classList.toggle('is-active', slide.getAttribute('data-media-id') === mediaId);
      });
      thumbs.forEach(function (thumb) {
        var isActive = thumb.getAttribute('data-media-id') === mediaId;
        thumb.classList.toggle('is-active', isActive);
        if (isActive && thumb.scrollIntoView) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      });
      if (galleryCounter) {
        var idx = mediaIds.indexOf(mediaId);
        if (idx !== -1) galleryCounter.textContent = idx + 1;
      }
    }

    function stepMedia(delta) {
      if (!mediaIds.length) return;
      var activeSlide = section.querySelector('[data-rv-gallery-main] [data-media-id].is-active');
      var currentId = activeSlide ? activeSlide.getAttribute('data-media-id') : mediaIds[0];
      var currentIdx = mediaIds.indexOf(currentId);
      if (currentIdx === -1) currentIdx = 0;
      var nextIdx = (currentIdx + delta + mediaIds.length) % mediaIds.length;
      activateMedia(mediaIds[nextIdx]);
    }

    if (galleryPrev) galleryPrev.addEventListener('click', function () { stepMedia(-1); });
    if (galleryNext) galleryNext.addEventListener('click', function () { stepMedia(1); });

    function applyVariant(variant) {
      if (!variant) return;
      if (hiddenIdInput) hiddenIdInput.value = variant.id;

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      var priceHtml = onSale
        ? '<span class="rv-price rv-price--sale"><span class="rv-price__sale">' + variant.formatted_price + '</span><span class="rv-price__compare">' + variant.formatted_compare_at_price + '</span></span>'
        : '<span class="rv-price"><span class="rv-price__regular">' + variant.formatted_price + '</span></span>';
      priceTargets.forEach(function (target) { target.innerHTML = priceHtml; });

      addButtons.forEach(function (btn) {
        if (variant.available) btn.removeAttribute('disabled');
        else btn.setAttribute('disabled', 'disabled');
      });
      addLabels.forEach(function (lbl) {
        lbl.textContent = variant.available ? 'Add to Bag' : 'Sold Out';
      });

      if (variant.featured_media_id) activateMedia(variant.featured_media_id);
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var variant = findMatchingVariant(currentSelection());
        applyVariant(variant);
      });
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activateMedia(thumb.getAttribute('data-media-id'));
      });
    });
  }

  /* --------------------------------------------------------------------
   * Product page: sticky mobile add-to-cart bar
   * ------------------------------------------------------------------ */
  function initStickyAddBar() {
    var bar = document.querySelector('[data-rv-sticky-add-bar]');
    var mainButton = document.getElementById('rv-main-add-to-cart');
    if (!bar || !mainButton || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // only show once the button has scrolled ABOVE the viewport —
          // not simply "not yet visible" on first load, before scrolling.
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', scrolledPast);
          bar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
        });
      },
      { threshold: 0 }
    );

    observer.observe(mainButton);
  }

  /* --------------------------------------------------------------------
   * Collection filters + sort — submit the form on any change
   * ------------------------------------------------------------------ */
  function initCollectionFilters() {
    var form = document.querySelector('[data-rv-filter-form]');
    if (!form) return;

    // the sort <select> lives outside the <form> and associates via the
    // form="" attribute, so its change events never bubble to `form` —
    // listen on the document and submit explicitly for both cases.
    document.addEventListener('change', function (e) {
      var isFilterField = form.contains(e.target) && e.target.matches('input[type="checkbox"], input[type="number"]');
      var isSortSelect = e.target.matches('[data-rv-sort-select]');
      if (isFilterField || isSortSelect) {
        form.submit();
      }
    });
  }

  /* --------------------------------------------------------------------
   * Collection grid density toggle (3 / 4 columns), persisted per browser
   * ------------------------------------------------------------------ */
  function initGridDensity() {
    var toggle = document.querySelector('[data-rv-density-toggle]');
    var grid = document.querySelector('[data-rv-density-grid]');
    if (!toggle || !grid) return;

    var buttons = toggle.querySelectorAll('[data-density]');
    var stored = window.localStorage ? window.localStorage.getItem('rv-grid-density') : null;

    function apply(cols) {
      grid.style.setProperty('--cols', cols);
      buttons.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-density') === String(cols));
      });
    }

    if (stored) apply(stored);

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cols = btn.getAttribute('data-density');
        apply(cols);
        if (window.localStorage) window.localStorage.setItem('rv-grid-density', cols);
      });
    });
  }

  /* --------------------------------------------------------------------
   * Product wishlist (favorite) toggle — persisted client-side per
   * browser via localStorage since the theme has no account-level
   * wishlist backend; scoped to product id so it survives page loads.
   * ------------------------------------------------------------------ */
  function initWishlist() {
    // querySelectorAll, not querySelector: the PDP has exactly one of
    // these buttons, but the catalog grid's product-card renders one per
    // card — same markup contract (data-rv-wishlist-btn + data-product-id),
    // just potentially many instances on one page.
    var buttons = document.querySelectorAll('[data-rv-wishlist-btn]');
    if (!buttons.length || !window.localStorage) return;

    var STORAGE_KEY = 'rv-wishlist';

    function readList() {
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    }

    buttons.forEach(function (btn) {
      var productId = btn.getAttribute('data-product-id');

      function setActive(isActive) {
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      }

      setActive(readList().indexOf(productId) !== -1);

      btn.addEventListener('click', function () {
        var list = readList();
        var index = list.indexOf(productId);
        if (index === -1) {
          list.push(productId);
        } else {
          list.splice(index, 1);
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        setActive(index === -1);
      });
    });
  }

  /* --------------------------------------------------------------------
   * Catalog grid masonry — greedy shortest-column placement, not CSS
   * multi-column: `column-count` + browser balancing turned out to cram
   * every card into the first column and leave the rest empty whenever
   * the product count was small (e.g. 2 products across 3 columns), no
   * matter how column-count itself was capped. This re-parents the
   * server-rendered cards (already in the markup, so nothing breaks for
   * no-JS) into real column wrapper divs, placing each card into
   * whichever column is currently shortest. Height is estimated from each
   * card's own image aspect ratio (set inline by product-card.liquid)
   * rather than measured layout height, since that's stable synchronously
   * — no need to wait on image decode or worry about measuring a card at
   * the wrong width before it's moved into its final column.
   * ------------------------------------------------------------------ */
  function initCatalogMasonry() {
    var grid = document.querySelector('[data-rv-masonry]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.children);
    if (!cards.length) return;

    function estimateHeight(card) {
      var media = card.querySelector('.rv-product-card__media');
      var ratio = media ? parseFloat(media.style.aspectRatio) : NaN;
      if (!ratio || isNaN(ratio)) ratio = 0.8;
      // 1/ratio approximates the image's own height relative to a shared
      // column width; the constant approximates the roughly-fixed text
      // block below it (title + price row + button row), which doesn't
      // scale with the photo the way the image itself does.
      return (1 / ratio) + 0.55;
    }

    function columnsForWidth() {
      var w = window.innerWidth;
      var base = w <= 560 ? 1 : w <= 989 ? 2 : 3;
      // never ask for more columns than there are cards — otherwise a
      // 2-product page still reserves a 3rd, permanently empty column.
      return Math.min(base, cards.length);
    }

    function layout() {
      var cols = columnsForWidth();
      var columns = [];
      var heights = [];
      for (var i = 0; i < cols; i++) {
        columns.push(document.createElement('div'));
        columns[i].className = 'rv-catalog__col';
        heights.push(0);
      }

      cards.forEach(function (card) {
        var shortest = 0;
        for (var i = 1; i < heights.length; i++) {
          if (heights[i] < heights[shortest]) shortest = i;
        }
        columns[shortest].appendChild(card);
        heights[shortest] += estimateHeight(card);
      });

      grid.textContent = '';
      columns.forEach(function (col) { grid.appendChild(col); });
      grid.classList.add('is-masonry');
    }

    layout();

    var currentCols = columnsForWidth();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var cols = columnsForWidth();
        if (cols !== currentCols) {
          currentCols = cols;
          layout();
        }
      }, 200);
    });
  }

  /* --------------------------------------------------------------------
   * Quick add — the catalog grid's always-visible "Add to Cart" button
   * and the hover-reveal tile button on other product-card grids both
   * share the data-rv-quick-add + data-variant-id contract. Reuses the
   * same postCart/refreshCartDrawer/updateCartCount helpers as the PDP
   * form above, just for a single default-variant product instead of a
   * full variant-picker form.
   * ------------------------------------------------------------------ */
  function initQuickAdd() {
    var buttons = document.querySelectorAll('[data-rv-quick-add]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId || btn.disabled) return;

        var label = btn.querySelector('[data-rv-quick-add-label]');
        var originalText = label ? label.textContent : btn.textContent;

        btn.disabled = true;
        if (label) label.textContent = 'Adding…';
        else btn.textContent = 'Adding…';

        postCart(routes.cartAdd, {
          id: variantId,
          quantity: 1,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname
        })
          .then(function (data) {
            refreshCartDrawer(data.sections);
            return fetch('/cart.js').then(function (r) { return r.json(); });
          })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            if (label) label.textContent = 'Added';
            else btn.textContent = 'Added';
            if (window.RV && window.RV.openCartDrawer) window.RV.openCartDrawer();
          })
          .catch(function () {
            if (label) label.textContent = 'Try Again';
            else btn.textContent = 'Try Again';
          })
          .finally(function () {
            setTimeout(function () {
              btn.disabled = false;
              if (label) label.textContent = originalText;
              else btn.textContent = originalText;
            }, 1800);
          });
      });
    });
  }

  /* --------------------------------------------------------------------
   * Product share — native share sheet where available, clipboard-copy
   * fallback everywhere else with a brief inline confirmation.
   * ------------------------------------------------------------------ */
  function initProductShare() {
    var btn = document.querySelector('[data-rv-share-btn]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var shareData = { title: document.title, url: window.location.href };
      if (navigator.share) {
        navigator.share(shareData).catch(function () {});
        return;
      }
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareData.url).then(function () {
          var label = btn.querySelector('[data-rv-share-label]');
          if (!label) return;
          var original = label.textContent;
          label.textContent = 'Link copied';
          window.setTimeout(function () {
            label.textContent = original;
          }, 2000);
        });
      }
    });
  }

  /* --------------------------------------------------------------------
   * Product recommendations — Shopify's recommendations engine only
   * populates `recommendations.performed` when a section is rendered via
   * its dedicated /recommendations/products endpoint (product_id + intent
   * params), not on a normal page load. So the section ships with an
   * empty placeholder and this fetches the real one client-side; if the
   * shop has no related products to show, the whole section hides.
   * ------------------------------------------------------------------ */
  function initProductRecommendations() {
    var containers = document.querySelectorAll('[data-rv-product-recommendations]');
    if (!containers.length) return;

    containers.forEach(function (el) {
      var url = el.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var temp = document.createElement('div');
          temp.innerHTML = html;
          var fresh = temp.querySelector('[data-rv-product-recommendations]');
          if (fresh && fresh.innerHTML.trim() !== '') {
            el.innerHTML = fresh.innerHTML;
            // product-card.liquid marks its images loading="lazy", which is
            // correct for a normal server-rendered page load — but this
            // markup is inserted well after the page has already loaded via
            // innerHTML, and browsers never pick up native lazy-loading on
            // images added this way (the intersection tracking that would
            // normally trigger the fetch on scroll never gets attached), so
            // every image here would silently sit at naturalWidth 0
            // forever. Forcing eager loading right after insertion is what
            // actually makes them render — the fetch itself is already
            // deferred, so eager-loading these specific images costs nothing.
            el.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
              img.loading = 'eager';
            });
            // the injected markup includes a [data-rv-slider] carousel that
            // didn't exist when animations.js ran its own init pass on page
            // load — re-run it now so the prev/next arrows and auto-drift
            // actually attach to it
            if (window.RV && window.RV.initEditorialSlider) window.RV.initEditorialSlider();
          } else {
            var section = el.closest('.rv-product-recs');
            if (section) section.style.display = 'none';
          }
        })
        .catch(function () {});
    });
  }

  /* --------------------------------------------------------------------
   * Contact form — inline blur-validation with a message per field,
   * error-state focus management after a failed server round-trip (this
   * form posts and reloads the page rather than submitting via AJAX), and
   * a disabled/"Sending…" state on a valid submit to prevent double-posts
   * during that reload.
   * ------------------------------------------------------------------ */
  function initContactForm() {
    var form = document.querySelector('.rv-contact__form');
    if (!form) return;

    var fields = Array.prototype.slice.call(form.querySelectorAll('.rv-field[required]'));

    function messageFor(field) {
      if (field.validity.valueMissing) return 'This field is required.';
      if (field.validity.typeMismatch && field.type === 'email') return 'Enter a valid email address.';
      return field.validationMessage || 'Please check this field.';
    }

    function validate(field) {
      var group = field.closest('.rv-field-group');
      if (!group) return true;
      var errorEl = group.querySelector('.rv-field-error');
      var valid = field.validity.valid;
      group.classList.toggle('has-error', !valid);
      field.setAttribute('aria-invalid', String(!valid));
      if (errorEl) errorEl.textContent = valid ? '' : messageFor(field);
      return valid;
    }

    fields.forEach(function (field) {
      field.addEventListener('blur', function () { validate(field); });
      field.addEventListener('input', function () {
        var group = field.closest('.rv-field-group');
        if (group && group.classList.contains('has-error')) validate(field);
      });
    });

    form.addEventListener('submit', function (e) {
      var allValid = true;
      fields.forEach(function (field) { if (!validate(field)) allValid = false; });

      if (!allValid) {
        e.preventDefault();
        var firstInvalid = fields.filter(function (f) { return !f.validity.valid; })[0];
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      var submitBtn = form.querySelector('.rv-contact__submit');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending…';
      }
    });

    // after a failed server-side submit (full page reload with form.errors
    // rendered), send focus to the first invalid field so keyboard/screen
    // reader users land on the problem immediately
    if (document.querySelector('.rv-contact__error')) {
      var firstBadField = form.querySelector('.rv-field[required]:invalid') || fields[0];
      if (firstBadField) firstBadField.focus();
    }
  }

  /* --------------------------------------------------------------------
   * boot
   * ------------------------------------------------------------------ */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initAddToCart();
    initCartLineEvents();
    initOptionLabels();
    initQuantityStepper();
    initVariantSelection();
    initCollectionFilters();
    initStickyAddBar();
    initGridDensity();
    initWishlist();
    initQuickAdd();
    initCatalogMasonry();
    initProductShare();
    initProductRecommendations();
    initContactForm();
  });
})();
